import React, { useEffect } from 'react';

import {
  Button,
  Group,
  Stack,
  TextInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import type { CareManager } from './types';
import { EMAIL_REGEX } from '../../utils/regex';
import { PostJSON } from '../../utils/request';

interface CareManagerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface CareManagerFormProps {
  initialData?: CareManager;
  mode?: 'add' | 'edit';
  onCancel?: () => void;
  onSuccess?: () => void;
}

interface CareManagerResponse {
  success: boolean;
  careManager: CareManager;
}

const CareManagerForm: React.FC<CareManagerFormProps> = ({
  initialData,
  mode = 'add',
  onCancel,
  onSuccess
}) => {
  const form = useForm<CareManagerFormData>({
    initialValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
      phone: initialData?.phone || ''
    },
    validate: {
      firstName: (value) => {
        if (!value || value.trim().length === 0) return 'First name is required';
        return null;
      },
      lastName: (value) => {
        if (!value || value.trim().length === 0) return 'Last name is required';
        return null;
      },
      email: (value) => {
        if (!value || value.trim().length === 0) return 'Email is required';
        if (!EMAIL_REGEX.test(value)) return 'Invalid email format';
        return null;
      }
    }
  });

  useEffect(() => {
    if (initialData) {
      form.setValues({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        phone: initialData.phone || ''
      });
    }
  }, [initialData]);

  const handleSubmit = async (values: CareManagerFormData) => {
    try {
      if (mode === 'edit' && initialData?.id) {
        await PostJSON<CareManagerResponse, Record<string, unknown>>(
          '/api/care_manager/edit',
          {
            id: initialData.id,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            phone: values.phone?.trim() || undefined
          }
        );
        notifications.show({
          title: 'Success',
          message: 'Care manager updated successfully',
          color: 'green'
        });
      } else {
        await PostJSON<CareManagerResponse, Record<string, unknown>>(
          '/api/care_manager/create',
          {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            phone: values.phone?.trim() || undefined
          }
        );
        notifications.show({
          title: 'Success',
          message: 'Care manager created successfully',
          color: 'green'
        });
        form.reset();
      }
      onSuccess?.();
    } catch (e) {
      notifications.show({
        title: 'Error',
        message: e instanceof Error ? e.message : `Failed to ${mode === 'edit' ? 'update' : 'create'} care manager`,
        color: 'red'
      });
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <TextInput
          required
          label="First Name"
          placeholder="Enter first name"
          {...form.getInputProps('firstName')}
        />
        <TextInput
          required
          label="Last Name"
          placeholder="Enter last name"
          {...form.getInputProps('lastName')}
        />
        <TextInput
          required
          label="Email"
          placeholder="Enter email"
          {...form.getInputProps('email')}
        />
        <TextInput
          label="Phone"
          placeholder="Enter phone number (optional)"
          {...form.getInputProps('phone')}
        />

        <Group justify="flex-end">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit">
            {mode === 'edit' ? 'Save Changes' : 'Create Care Manager'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default CareManagerForm;
